import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Redux action for sorting
import { handleSorting } from 'store/Admin/OrderList';

// Hooks & utils
import { usePagination } from 'utils/hooks/usePagination';
import { formatDate } from 'utils/DateUtils';
import {
  ORDERSTATUS,
  pageNbOptions,
} from 'utils/Constants';
import { filterOrders } from 'helpers/FilterOrders';

// UI Components
import { Paginator } from 'components/shared/Paginator';
import { Loader } from 'components/shared/Loader';
import ErrorComponent from 'components/shared/ErrorComponent';
import { CustomDropdownReadOnly } from 'components/shared/DropDown/CustomDropdown';
import SearchIcon from 'icons/SearchIcon';
import { Button } from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon } from 'icons/ChevronIcon';
import DataTable from 'components/shared/DataTable';
import axiosPrivate from 'config/axiosPrivate';
import '../../../pages/AdminClient/project.css';

export default function WriterClientProjectTable({
  title,
  orderStatus,
  orders,
  isLoading,
  isError,
  fetchDisplayData,
  onRowClickRedirectPath,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [titleSearch, setTitleSearch] = useState('');
  const [titleFilteredOrders, setTitleFilteredOrders] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [anchorElManager, setAnchorElManager] = useState(null);

  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
  }, []);

  // Filter orders by title search
  useEffect(() => {
    if (!orders) return;
    let filtered = filterOrders(orders, null, titleSearch, true);
    if (titleSearch.length > 0) setCurrentPage(1);
    setTitleFilteredOrders(filtered);
  }, [orders, titleSearch]);

  // Pagination
  const { currentPage, setCurrentPage, totalPages, paginatedItems } =
    usePagination(titleFilteredOrders, ordersPerPage);

  // Format rows
  const rows = paginatedItems.map((order) => ({
    ...order,
    title: order.title,
    deadline: order.deadline ? formatDate(order.deadline) : null,
  }));

  // Manager handlers
  const handleManagerClick = (idx, e) => setAnchorElManager({ [idx]: e.currentTarget });
  const handleManagerClose = () => setAnchorElManager(null);

  const handleRowClick = (order) => {
    // If no action is requested, do nothing on row click
    if (!fetchDisplayData && !onRowClickRedirectPath) return;

    if (fetchDisplayData) {
      fetchDisplayData(order.id, order.type);
    }
    try {
      window.localStorage.setItem(
        'WriterClient_ProjectDisplay_fromCategory',
        orderStatus
      );
      window.localStorage.setItem(
        'WriterClient_ProjectDisplay_id',
        String(order.id)
      );
    } catch { }
    if (onRowClickRedirectPath) {
      history.push(onRowClickRedirectPath);
    }
  };

  // Toolbar
  const toolbar = (
    <div className="box-info-header">
      <div className="b-child-1">
        <h1>{title}</h1>
      </div>
      <div className="b-child-2 d-flex justify-content-end align-items-center">
        <div className="box-input-text pe-0" style={{ marginRight: 12 }}>
          <div className="input-group input-cs w-100 pe-0">
            <div className="input-group-text">
              <SearchIcon />
            </div>
            <input
              type="text"
              className="form-control ps-0"
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              placeholder={t('Rechercher')}
            />
          </div>
        </div>
        <div className="dropdown" style={{ width: '70px' }}>
          <button
            className="btn dropdown-toggle dropdown-custom w-100"
            type="button"
            id="dropdownMenuButton1"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {ordersPerPage}
            <ChevronDownIcon />
          </button>
          <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
            {pageNbOptions
              .filter((nb) => nb !== ordersPerPage)
              .map((item) => (
                <li key={item}>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => {
                      setOrdersPerPage(item);
                      setCurrentPage(1);
                    }}
                  >
                    {item}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );

  // Columns definition
  const columns = [
    {
      header: t('Titre projet'),
      accessor: 'title',
      sortable: true,
      width: '20%',
    },
    {
      header: t('Chef de projet'),
      accessor: 'projectManager',
      sortable: false,
      width: '15%',
      renderCell: (order, idx) => {
        const managers = order?.projectManager ? [order.projectManager] : [];

        if (managers.length === 0) {
          return <Button disabled>{t('Non attribués')}</Button>;
        }

        return (
          <>
            <Button onClick={(e) => handleManagerClick(idx, e)} key={`btn-manager-${idx}`}>
              <img src="/Images/icon-users-down.png" alt="Manager" />
            </Button>

            {anchorElManager?.[idx] && (
              <CustomDropdownReadOnly
                key={`dropdown-manager-${idx}`}
                handleClose={() => handleManagerClose(idx)}
                anchorEl={anchorElManager[idx]}
                list={managers}
                getOptionLabel={(opt) =>
                  opt?.mail ||
                  [opt?.firstName, opt?.lastName].filter(Boolean).join(' ') ||
                  `ID: ${opt?.id}`
                }
              />
            )}
          </>
        );
      },
    },
    {
      header: t('Deadline'),
      accessor: 'deadline',
      sortable: true,
      width: '15%',
    },
    {
      header: t('Site web'),
      accessor: 'websiteUrl',
      sortable: true,
      width: '20%',
    },
    {
      header: t('Livrés'),
      accessor: 'deliveredTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/delivred-text-icon.png"
            alt={t('Livrés')}
            style={{ width: 16, height: 16 }}
          />
          <span>{o.deliveredTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
    {
      header: t('Validés'),
      accessor: 'validatedTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/validate-text-icon.png"
            alt={t('Validés')}
            style={{ width: 16, height: 16 }}
          />
          <span>{o.validatedTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
    {
      header: t('En ligne'),
      accessor: 'deployedTextsAmount',
      sortable: true,
      renderCell: (o) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500 }}>
          <img
            src="/Images/deployed-text-icon.png"
            alt={t('En ligne')}
            style={{ width: 16, height: 16 }}
          />
          <span>{o.deployedTextsAmount || 0} / {o.textAmount || 0}</span>
        </div>
      ),
    },
  ];

  // Sorting handler
  const handleSortingChange = (field) => {
    const direction =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(
      handleSorting({
        table: orderStatus,
        sortField: field,
        sortOrder: direction,
      })
    );
    setSortField(field);
    setSortOrder(direction);
  };

  // Render: loading, error, or main content
  return (
    <div className="content-center">
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorComponent />
      ) : (
        <>
          {/* Toolbar */}
          {toolbar}

          {/* Data Table */}
          <DataTable
            columns={columns}
            data={rows}
            enableSorting
            onRowClick={handleRowClick}
            onSort={handleSortingChange}
            sortField={sortField}
            sortOrder={sortOrder}
          />

          {/* Paginator */}
          <Paginator
            isLoading={isLoading}
            listLength={titleFilteredOrders.length}
            currentPage={currentPage}
            pageNumbers={[...Array(totalPages).keys()].map((i) => i + 1)}
            nbElementsPerPage={ordersPerPage}
            goToPage={setCurrentPage}
          />
        </>
      )}
    </div>
  );
}

